import { Crown } from 'lucide-react';
import { AmountDisplay } from '@/components/common/AmountDisplay';
import type { Group } from '@sharecost/shared';
import { useTranslation } from 'react-i18next';

interface BudgetLockedNoticeProps {
  group: Pick<
    Group,
    'budget_amount_minor' | 'budget_period' | 'currency_code'
  >;
}

/**
 * Rendered inside the group detail page when the group has a budget configured
 * but the current viewer has no premium access (i.e. neither they nor any
 * other member is Pro). We still show the target amount + period so the user
 * understands what's hidden behind the gate, but no live progress.
 */
export function BudgetLockedNotice({ group }: BudgetLockedNoticeProps) {
  const { t } = useTranslation();
  const amount = group.budget_amount_minor ?? 0;
  const periodLabel = group.budget_period === 'monthly' ? t('web:budgetLockedNotice.monthly') : t('web:budgetLockedNotice.total');

  return (
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
        <Crown className="h-4 w-4 text-amber-600 dark:text-amber-400" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-text-primary">{t('web:budgetLockedNotice.budgetTrackingIsAProFeature')}</h3>
        </div>
        <p className="mt-0.5 text-xs text-text-tertiary">
          {t('web:budgetLockedNotice.thisGroupHasBudget', { period: periodLabel })}{' '}
          <span className="font-medium text-text-secondary">
            <AmountDisplay amountMinor={amount} currency={group.currency_code} />
          </span>
          . {t('web:budgetLockedNotice.upgradeAnyMember')}
        </p>
      </div>
    </div>
  );
}
